import React, { useState, useContext, useMemo } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import '../css/pors/doc_process.css';
import { Button, Table, Form, InputGroup, Pagination } from 'react-bootstrap';
import { MdEdit, MdLocalPrintshop, MdBlockFlipped, MdSearch, MdUnfoldMore, MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { ToastContainer, toast, Zoom } from 'react-toastify';
import { Editdoc } from '../edit/editdoc';
import { Printdoc } from '../print/printdoc';
import { Reason } from '../reason/reason';
import { Dimcontext } from "../../context/pagecontext";
import { AuthContext } from '../../context/authContext';
import Swal from 'sweetalert2';
import { motion, AnimatePresence } from 'framer-motion';
import { Datacontext } from '../../context/dataContext';

const HomeDoc = () => {
  const { setDim } = useContext(Dimcontext);
  const { documents, fetchDocuments, loading } = useContext(Datacontext);
  const { user } = useContext(AuthContext);
  
  const [docedit, set_docedit] = useState(false);
  const [docid, set_docid] = useState('');
  const [docview, setdocview] = useState(false);
  const [reason, set_reason] = useState(false);
  const [appno, set_appno] = useState('');

  // --- Pagination & Search States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'application_no', direction: 'desc' });

  const docs = useMemo(() => {
    if (!documents) return [];

    const processingDocs = documents.filter(d => d.docstatus === "processing");

    if (user?.role === "user") {
      return processingDocs.filter(d => 
        // FIXED: Safe array fallback prevents .includes() from crashing
        (d?.managed_By || []).map(name => name.toLowerCase()).includes(user.name.toLowerCase())
      );
    }

    return processingDocs;
  }, [documents, user]);

  // --- Optimized Filtering & Sorting ---
  const processedData = useMemo(() => {
    let filtered = docs.filter(doc => {
      const buyerName = doc?.buyers?.[0]?.buyer_Detail?.name || "";
      const searchStr = `${buyerName} ${doc.application_no} ${doc.register_office} ${doc.village}`.toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        // FIXED: Added ?.[0] to safely access arrays that might be undefined
        let aValue = sortConfig.key === 'name' ? a?.buyers?.[0]?.buyer_Detail?.name : a[sortConfig.key];
        let bValue = sortConfig.key === 'name' ? b?.buyers?.[0]?.buyer_Detail?.name : b[sortConfig.key];
        
        // FIXED: Force strings and lowercases for accurate alphabetical sorting
        aValue = (aValue || "").toString().toLowerCase();
        bValue = (bValue || "").toString().toLowerCase();
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [docs, searchTerm, sortConfig]);

  // --- Manual Pagination Logic ---
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = processedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(processedData.length / rowsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const calculateProgress = (doc) => {
    if (!doc) return 0;

    // Presenceless Online/Offline (not docstatus)
    const rawStatus = typeof doc.status === 'string'
      ? doc.status
      : (doc.status?.status || 'offline');
    const isOnline = String(rawStatus).toLowerCase() === 'online';

    // --- STAGE 1: Reference & Applicant ---
    const isStep1Complete = !!(
      doc.zone && 
      doc.register_office &&
      doc.document_date &&
      doc.register_document &&
      doc.managed_By?.length > 0 && doc.managed_By.every(Boolean) && 
      doc.refered_by && 
      doc.buyers?.length > 0 && doc.buyers.every(b => !!b.buyer_Detail?.name) && 
      doc.sellers?.length > 0 && doc.sellers.every(s => !!s.seller_Detail?.name) && 
      doc.witness?.length > 0 && doc.witness.every(w => !!w.witness_Detail?.name)
    );

    // --- STAGE 2: Property & Land ---
    const isStep2Complete = !!(
      doc.village && 
      doc.layout_name && 
      doc.approximate_quatation && 
      doc.sfno?.trim() !== "" && 
      doc.site_no?.trim() !== "" && 
      doc.parent_document?.length > 0 && 
      doc.parent_document.every(d => d.type !== "" && d.number?.trim() !== "")
    );

    // --- STAGE 3: Guideline Value ---
    const isStep3Complete = !isOnline 
      ? (doc.value_check?.values === 'yes' || doc.value_check?.values === 'no') 
      : (doc.value_check?.values === 'no' || (doc.value_check?.values === 'yes' && doc.value_check?.doc_value?.trim() !== ""));

    // --- STAGE 4: Offline Draft (skipped for Presenceless Online) ---
    const isStep4Complete = !!(
      doc.draft_prepared?.values && 
      (doc.draft_send?.values === "yes" 
          ? doc.draft_send?.through?.trim() !== "" && (
              doc.draft_send?.through === "online" 
                  ? !!(doc.draft_send?.file || doc.draft_send?.fileData?.url)
                  : true
            )
          : doc.draft_send?.values
      ) && 
      (doc.draft_approved?.values === 'no' ? true : (doc.draft_approved?.values === 'yes' && doc.draft_approved?.by?.trim() !== ""))
    );

    // --- STAGE 5: EC Process ---
    const isCertificateReceivedComplete = doc.certificate_received?.values === 'yes'
      ? !!(doc.certificate_received?.file || doc.certificate_received?.fileData?.url)
      : doc.certificate_received?.values;
    const isStep5Complete = doc.already_applied_ec === 'yes'
      ? !!(doc.linked_ec?.id && isCertificateReceivedComplete)
      : !!(
        (doc.certificate_applied?.values === 'yes' ? doc.certificate_applied?.eca_no?.trim() !== "" : doc.certificate_applied?.values) && 
        isCertificateReceivedComplete
      );

    // --- STAGE 6: Online Draft (Token hidden for Presenceless Online) ---
    const isDeedComplete = !!(doc.deed_no?.values === 'yes' ? doc.deed_no?.tp_no?.trim() !== "" : doc.deed_no?.values);
    const isTokenComplete = !!(
      doc.token_initiated?.values === 'yes' 
        ? (doc.token_initiated?.token_no?.trim() !== "" && doc.token_initiated?.time && !!(doc.token_initiated?.file || doc.token_initiated?.fileData?.url)) 
        : doc.token_initiated?.values
    );
    const isStep6Complete = isOnline ? isDeedComplete : !!(isDeedComplete && isTokenComplete);

    // --- STAGE 7: Registration Process (Stamp Paper optional when No) ---
    const isStampPaperComplete = !doc.stamp_Paper?.length
      || doc.stamp_Paper.every(s => s.value !== "" && s.number?.trim() !== "");
    const isStep7Complete = !!(
      isStampPaperComplete && 
      doc.payment_Info?.trim() && 
      (isOnline || (doc.document_pending?.values === 'yes' ? doc.document_pending?.document_no?.trim() !== "" : doc.document_pending?.values)) &&  
      (doc.document_registered?.values === 'yes' ? doc.document_registered?.document_no?.trim() !== "" : doc.document_registered?.values)
    );

    // --- STAGE 8: Return Collection ---
    // Presenceless Online: Collection Status hidden — only scanned/downloaded required
    const isScannedComplete = doc.document_scanned?.values === 'yes'
      ? !!(doc.document_scanned?.file || doc.document_scanned?.fileData?.url)
      : doc.document_scanned?.values;
    const isStep8Complete = isOnline
      ? !!isScannedComplete
      : !!(doc.document_collected?.values && isScannedComplete);

    // --- STAGE 9: Application Completed ---
    const isStep9Complete = !!(
      doc.payment?.values && 
      (doc.other?.values === 'yes' ? !!doc.other?.cusDetail?.name : doc.other?.values === 'no')
    );

    // --- PROGRESS CALCULATION ---
    // Presenceless Online: exclude Offline Draft (not in available fields)
    const stages = isOnline
      ? [
          isStep1Complete, isStep2Complete, isStep3Complete,
          isStep5Complete, isStep6Complete,
          isStep7Complete, isStep8Complete, isStep9Complete
        ]
      : [
          isStep1Complete, isStep2Complete, isStep3Complete,
          isStep4Complete, isStep5Complete, isStep6Complete,
          isStep7Complete, isStep8Complete, isStep9Complete
        ];

    const completedCount = stages.filter(Boolean).length;
    return Math.floor((completedCount / stages.length) * 100);
  };

  const handleAction = async(type, id, app) => {
    set_docid(id);
    setDim(true);

    if (type === 'edit') set_docedit(true);

    if (type === 'print') { 
      const docToPrint = documents.find(item => item.id === id);
      const progress = calculateProgress(docToPrint);
      
      if (progress === 100){
        setdocview(true);
        setDim(true);
        set_docid(id);
      } else {
        Swal.fire({
          title: "INCOMPLETE",
          html: `Current Progress is <b>${progress}%</b>.<br/>Please complete all document processes before printing.`,
          icon: "warning",
          width: "28em",
          scrollbarPadding: false,
          customClass: {
            confirmButton: "my-confirm-btn",
          }
        });
        setDim(false);
      }
    }

    if (type === 'reject') { 
      set_reason(true); 
      set_appno(app); 
    }
  };

  const getBadgeColor = (name) => {
    const variants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info'];
    const index = name.length % variants.length;
    return variants[index];
  };

  const showToast = (msg) => 
    toast.warn(msg, {
      position: "bottom-center",
      autoClose:3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      transition: Zoom,
    });

  const showsToast = (msg) => 
    toast.success(msg, {
      position: "bottom-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "dark",
      transition: Zoom,
     });

  return (
    <div className="home-layout-wrapper p-0 p-md-4" >
      <ToastContainer />
      
      <div className="main-glass-card shadow-sm">
        <div className="card-header-custom d-flex justify-content-between align-items-center p-4">
          <h4 className="m-0 fw-bold text-dark">Processing Documents</h4>
          
          <div className="d-flex gap-3">
            <InputGroup className="search-bar">
              <InputGroup.Text className="bg-white border-end-0">
                <MdSearch size={20} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search name, app no..."
                className="border-start-0 ps-0"
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </InputGroup>
          </div>
        </div>

        <div className="table-side-scroll-container">
          <Table hover striped responsive className="imtable">
            <thead>
              <tr>
                <th className="sticky-col pin-no">NO.</th>
                <th className="sticky-col pin-name cursor-pointer" onClick={() => handleSort('name')}>
                  NAME <MdUnfoldMore className="ms-1" />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('application_no')}>
                  APP NO <MdUnfoldMore className="ms-1" />
                </th>
                <th>
                  NATURE
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('document_date')}>
                  DATE <MdUnfoldMore className="ms-1" />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('register_office')}>
                  OFFICE <MdUnfoldMore className="ms-1" />
                </th>
                <th>MANAGED BY</th>
                <th>PROCESS</th>
                <th className="text-center">ACTION</th>
              </tr>
            </thead>
            <tbody>
              { loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <div className="spinner-container">
                      <div className="custom-loader"></div>
                      <div className="mt-2 text-muted small">Loading records...</div>
                    </div>
                  </td>
                </tr>
              ) : currentRows.length > 0 ? currentRows.map((doc, index) => (
                <tr key={doc.id}>
                  <td className="sticky-col pin-no">{indexOfFirstRow + index + 1}</td>
                  <td className="sticky-col pin-name fw-semibold">{doc?.buyers?.[0]?.buyer_Detail?.name?.toUpperCase() || 'N/A'}</td>
                  <td>
                    <span className="app-badge">{doc.application_no}</span>
                    {doc.document_pending?.values === "yes" && (
                      <span 
                        className="badge rounded-pill bg-success ms-1" 
                        style={{ fontSize: '0.60rem', verticalAlign: 'top', padding: '2px 5px' }}
                      >
                        p
                      </span>
                    )}
                  </td>
                  <td><div className="text-truncate" style={{maxWidth: '150px'}}>{doc.register_document?.toUpperCase() || 'N/A'}</div></td>
                  
                  {/* FIXED: Added a safety check to gracefully handle missing/invalid dates */}
                  <td>{doc.document_date ? new Date(doc.document_date).toLocaleDateString("en-GB").replaceAll("/", "-") : 'N/A'}</td>
                  
                  <td>{doc.register_office?.toUpperCase() || 'N/A'}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                      {/* FIXED: Array check prevents crashes if the array doesn't exist */}
                      {Array.isArray(doc.managed_By) && doc.managed_By.map((manager, i) => (
                        <span 
                          key={i} 
                          className={`badge rounded-pill bg-${getBadgeColor(manager)}`}
                          style={{ fontSize: '0.60rem' }}
                        >
                          {manager.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div style={{ width: 45, height: 45 }}>
                      <CircularProgressbar 
                        value={calculateProgress(doc)} 
                        text={`${calculateProgress(doc)}%`}
                        styles={buildStyles({
                          textSize: '28px',
                          pathColor: '#4a127e',
                          textColor: '#4a127e',
                          trailColor: '#f1f1f1'
                        })}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      <Button className="btn-action be" onClick={() => handleAction('edit', doc.id)}><MdEdit /></Button>
                      <Button className="btn-action bp" onClick={() => handleAction('print', doc.id)}><MdLocalPrintshop /></Button>
                      <Button className="btn-action br" onClick={() => handleAction('reject', doc.id, doc.application_no)}><MdBlockFlipped /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={9} className="text-center py-5 text-muted">No processing documents found</td></tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* --- Pagination Footer --- */}
        <div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
          
          <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">Rows per page:</span>
              <Form.Select 
                  size="sm" 
                  style={{ width: '80px', borderRadius: '6px' }}
                  value={rowsPerPage} 
                  onChange={(e) => { 
                      setRowsPerPage(Number(e.target.value)); 
                      setCurrentPage(1); 
                  }}
              >
                  {[5, 10, 20, 50].map(v => (
                      <option key={v} value={v}>{v}</option>
                  ))}
              </Form.Select>
          </div>

          <div className="text-muted small fw-medium">
              {processedData.length > 0 ? (
                  <>Showing <b>{indexOfFirstRow + 1}</b> to <b>{Math.min(indexOfLastRow, processedData.length)}</b> of <b>{processedData.length}</b> entries</>
              ) : "No entries found"}
          </div>

          <Pagination className="mb-0 custom-pagination shadow-sm">
              <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
              <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
                  <MdKeyboardArrowLeft size={20} />
              </Pagination.Prev>
              <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>
              <Pagination.Next disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
                  <MdKeyboardArrowRight size={20} />
              </Pagination.Next>
              <Pagination.Last disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(totalPages)} />
          </Pagination>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {(docedit || docview || reason) && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Edit Modal */}
            {docedit && (
              <motion.div
                key="edit-modal"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="modal-content-box"
              >
                <Editdoc 
                  set_docedit={set_docedit} 
                  id={docid} 
                  showToast={showToast} 
                  onclose={fetchDocuments} 
                />
              </motion.div>
            )}

            {/* Print Modal */}
            {docview && (
              <motion.div
                key="print-modal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="modal-content-box"
              >
                <Printdoc 
                  setdocview={setdocview} 
                  showsToast={showsToast} 
                  id={docid} 
                  onclose={fetchDocuments} 
                />
              </motion.div>
            )}

            {/* Reason Modal */}
            {reason && (
              <motion.div
                key="reason-modal"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 100 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                className="modal-content-box side-panel"
              >
                <Reason 
                  set_reason={set_reason} 
                  showsToast={showsToast} 
                  id={docid} 
                  appno={appno} 
                  onclose={fetchDocuments} 
                  TitleCase={(s) => s} 
                />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomeDoc;